import { fg } from '@confluence/feature-gating';
import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import useConnectorOnboardingStateQuery, {
	type useConnectorOnboardingStateQuery$data,
	type useConnectorOnboardingStateQuery$variables,
} from './hooks/__generated__/useConnectorOnboardingStateQuery.graphql.ts';
import useConnectorOnboardingTeamTypeQuery, {
	type useConnectorOnboardingTeamTypeQuery$data,
	type useConnectorOnboardingTeamTypeQuery$variables,
} from './hooks/__generated__/useConnectorOnboardingTeamTypeQuery.graphql.ts';
import useConnectorStatesQuery, {
	type useConnectorStatesQuery$data,
	type useConnectorStatesQuery$variables,
} from './hooks/__generated__/useConnectorStatesQuery.graphql.ts';
import { isWithinEnrolmentWindow } from './utils/enrolmentWindow.ts';

// User should not be an admin when using this preload
export const preloadConnectorOnboardingState = async (userId?: string): Promise<void> => {
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (!fg('twcg-1489-cc-3p-connector-onboarding-rollout')) {
		return;
	}

	const { isRovoEnabled, orgId, cloudId } = getPreloaderFnContext();
	if (!isRovoEnabled) {
		return;
	}

	// Always preload onboarding state first. It is lightweight and tells us
	// whether the more expensive AGG queries are still needed.
	const onboardingResult = await queryAgg<
		useConnectorOnboardingStateQuery$data,
		useConnectorOnboardingStateQuery$variables
	>({
		query: useConnectorOnboardingStateQuery,
		variables: {},
	}).catch(() => null);

	// Extract the enrolment date and skip the AGG queries when the experiment
	// is past its active window.
	const enrolmentDateStr =
		onboardingResult?.data?.userPreferences?.onboardingState?.find(
			(entry) => entry.key === TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE,
		)?.value ?? undefined;

	if (!isWithinEnrolmentWindow(enrolmentDateStr)) {
		return;
	}

	// The sidebar is the only remaining promo surface. Once it has been dismissed
	// (on its own or as part of 'both') nothing can render, so skip the AGG queries.
	const promoDismissed = onboardingResult?.data?.userPreferences?.onboardingState?.find(
		(entry) => entry.key === TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED,
	)?.value;

	if (promoDismissed === 'sidebar' || promoDismissed === 'both') {
		return;
	}

	// Within the window — fire the AGG queries in parallel.
	const tasks: Array<Promise<unknown>> = [];

	if (userId) {
		tasks.push(
			queryAgg<
				useConnectorOnboardingTeamTypeQuery$data,
				useConnectorOnboardingTeamTypeQuery$variables
			>({
				query: useConnectorOnboardingTeamTypeQuery,
				variables: { userId },
			}),
		);
	}

	if (orgId && cloudId) {
		tasks.push(
			queryAgg<useConnectorStatesQuery$data, useConnectorStatesQuery$variables>({
				query: useConnectorStatesQuery,
				variables: { orgId, resourceId: cloudId, first: 75 },
			}),
		);
	}

	if (tasks.length > 0) {
		await waitForAllReturnWhenError(tasks);
	}
};
